import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Box,
  Container,
  Stack,
  IconButton,
  Divider,
  Snackbar,
  Alert
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import { Cancel, Close } from '@mui/icons-material';


const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 'bold',
          backgroundColor: '#f5f5f5',
        },
      },
    },
  },
});

const CompanyManagement = () => {
  const [screen, setScreen] = useState('read');
  const [item, setItem] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Form state for create and edit forms
  const [formValues, setFormValues] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
  });

  useEffect(() => {
    fetchLocations();
    fetchCompanies();
  }, []);

  // Reset form when switching screens
  useEffect(() => {
    if (screen === 'write') {
      setFormValues({
        name: '',
        email: '',
        phone: '',
        location: '',
      });
    } else if (screen === 'modify' && item) {
      setFormValues({
        name: item.name || '',
        email: item.email || '',
        phone: item.phone || '',
        location: item.location || '',
      });
    }
  }, [screen, item]);

  const fetchCompanies = async () => {
    try {
      const response = await fetch('http://biometick.com/get-company/' + localStorage.getItem("userID"), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.length > 0) {
        setCompanies(data);
      }
    } catch (error) {
      showSnackbar('Error fetching companies', 'error');
      console.error(error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await fetch('http://biometick.com/get-locations/' + localStorage.getItem("userID"), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.length > 0) {
        setLocations(data);
      }
    } catch (error) {
      showSnackbar('Error fetching locations', 'error');
      console.error(error);
    }
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleCreate = async () => {
    try {
      const { name, email, phone, location } = formValues;

      var myHeaders = new Headers();
      myHeaders.append("Authorization", `Bearer ${localStorage.getItem("token")}`);
      myHeaders.append("Content-Type", "application/x-www-form-urlencoded");

      var urlencoded = new URLSearchParams();
      urlencoded.append("name", name);
      urlencoded.append("email", email);
      urlencoded.append("userID", localStorage.getItem("userID"));
      urlencoded.append("phone", phone);
      urlencoded.append("location", location);

      var requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: urlencoded,
        redirect: 'follow'
      };

      const response = await fetch("http://biometick.com/post-company", requestOptions);
      const result = JSON.parse(await response.text());

      if (result.hasOwnProperty('_id')) {
        showSnackbar('Company created successfully!', 'success');
        await fetchCompanies();
        setScreen('read');
      } else {
        showSnackbar('Error creating company', 'error');
      }
    } catch (error) {
      showSnackbar('Error creating company', 'error');
      console.error(error);
    }
  };

  const handleUpdate = async (id) => {
    try {
      const response = await fetch(`http://biometick.com/modify-company/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formValues)
      });

      const result = JSON.parse(await response.text());

      if (result.hasOwnProperty('_id')) {
        showSnackbar('Company updated successfully!', 'success');
        await fetchCompanies();
        setScreen('read');
      } else {
        showSnackbar('Error updating company', 'error');
      }
    } catch (error) {
      showSnackbar('Error updating company', 'error');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`http://biometick.com/delete-company/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        showSnackbar('Company deleted successfully!', 'success');
        setCompanies(companies.filter(company => company._id !== id));
      } else {
        showSnackbar('Error deleting company', 'error');
      }
    } catch (error) {
      showSnackbar('Error deleting company', 'error');
      console.error(error);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  const CompanyList = () => (
    <TableContainer component={Paper} elevation={2} sx={{ mt: 3, borderRadius: 2, overflow: 'hidden' }}>
      <Table aria-label="department table">
        <TableHead>
          <TableRow>
            <TableCell>Company Name</TableCell>
            <TableCell>Location</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Phone</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {companies.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center">No companies found</TableCell>
            </TableRow>
          ) : (
            companies.map((company) => (
              <TableRow key={company._id} hover>
                <TableCell>{company.name}</TableCell>
                <TableCell>
                  {company.location
                    ? locations.find(location => location._id === company.location)?.name || 'Unknown'
                    : 'N/A'}
                </TableCell>
                <TableCell>{company.email}</TableCell>
                <TableCell>{company.phone}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} justifyContent="center">
                    <IconButton
                      color="primary"
                      onClick={() => {
                        setItem(company);
                        setScreen('modify');
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(company._id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const CompanyForm = ({ isEdit }) => (
    <Box sx={{ mt: 3, width: '100%' }}>
      <Card elevation={3}>
        <CardContent>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Company Name"
                name="name"
                variant="outlined"
                fullWidth
                value={formValues.name}
                onChange={handleFormChange}
                required
              />
              <TextField
                label="Email"
                name="email"
                variant="outlined"
                fullWidth
                value={formValues.email}
                onChange={handleFormChange}
                type="email"
                required
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Phone"
                name="phone"
                variant="outlined"
                fullWidth
                value={formValues.phone}
                onChange={handleFormChange}
                type="tel"
                required
              />
              <TextField
                select
                label="Location"
                name="location"
                variant="outlined"
                fullWidth
                value={formValues.location}
                onChange={handleFormChange}
                required
              >
                {locations.map((option) => (
                  <MenuItem key={option._id} value={option._id}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => setScreen('read')}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={isEdit ? <SaveIcon /> : <AddCircleIcon />}
                onClick={isEdit ? () => handleUpdate(item._id) : handleCreate}
                color="primary"
              >
                {isEdit ? 'Update' : 'Create Company'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );

  return (<ThemeProvider theme={theme}>
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" color="primary">
            {screen === 'read' && 'Company'}
            {screen === 'write' && 'Create Company'}
            {screen === 'modify' && 'Edit Company'}
          </Typography>

          {screen === 'read' ? <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setScreen('write');
            }}
          >
            Add Company
          </Button> : <Button
            variant="contained"
            color="warning"
            startIcon={<Close />}
            onClick={() => {
              setScreen('read');
            }}
          >
            Cancel
          </Button>}
        </Box>
        <Divider sx={{ mt: 2 }} />
      </Box>

      {screen === 'read' && <CompanyList />}
      {screen === 'write' && <CompanyForm isEdit={false} />}
      {screen === 'modify' && <CompanyForm isEdit={true} />}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  </ThemeProvider>);
};

export default CompanyManagement;